import type { AhqSubmission } from '@axiom/validation';
import { Api } from '@axiom/ui';

import { EnvUtil } from '../utils/env-util';

/**
 * Shaped candidate patch matching the backend updateSubmissionValidator schema.
 * Uses candidateId (not id) and only allows specific fields (additionalProperties: false).
 * Each entry must include at least one of: isActiveForSubmission, submissionRank,
 * submissionDescription, or highlightedExperiences.
 */
type SubmissionCandidatePatch = {
  candidateId: string;
  isActiveForSubmission?: boolean;
  submissionRank?: number;
  submissionDescription?: string;
  showCandidateReputation?: boolean | null;
  candidateReputationClarification?: {
    submissionComments: string;
  };
  highlightedExperiences?: Array<{
    id: string;
    experienceVisible: boolean;
    isHighlighted?: boolean | null;
    submissionDescription?: string | null;
  }> | null;
};

type SubmissionUpdatePayload = {
  newSubmissionType?: boolean;
  candidates: SubmissionCandidatePatch[];
};

class SubmissionsApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
    });
  }

  readSubmission(submissionId: AhqSubmission['submissionId']) {
    return super.read<{ data: AhqSubmission }>({
      endpoint: `/submissions/${submissionId}`,
      method: 'GET',
    });
  }

  async refreshSubmission(submissionId: AhqSubmission['submissionId']) {
    return super.invalidate(`/submissions/${submissionId}`);
  }

  async updateSubmission(
    submissionId: AhqSubmission['submissionId'],
    body: SubmissionUpdatePayload
  ) {
    return super.write<{ data: null }>({
      endpoint: `/submissions/${submissionId}`,
      method: 'PATCH',
      body,
    });
  }
}

export const SubmissionsApi = new SubmissionsApiClass();
